import { callApi, ThrowError } from "@/api/callApi";
import { API_ENDPOINT, API_URL_CMS_SERVICE } from "@/api/constant";
import CommonTable, { TableColumn } from "@/components/common/CommonTable";
import ConfirmationModel from "@/components/common/models/ConfrimationModel";
import Model from "@/components/common/models/Model";
import StatusDropdown from "@/components/common/StatusComponent";
import Layout from "@/components/Layout/Layout";
import Card from "@/components/UI/Card";
import FormField from "@/components/UI/FormField";
import { getPermissionName, PERMISSIONS } from "@/context/permission";
import { usePermissions } from "@/hooks/usePermissions";
import { IAppLayout } from "@/interfaces/appLayout.interface";
import { ActionConfig } from "@/interfaces/common.interface";
import { Info, Plus } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

interface StateProps {
    isLoading: boolean
    data: IAppLayout[];
    selectedLayout: IAppLayout | null
    showEditModel: boolean
    error: string | null
    updatedTitle: string | null
    statusToUpdate: string | null
    showUpdateModel: boolean
}

const AppLayout = () => {
    const { canEdit } = usePermissions()
    const includedKeys = [
        'categories',
        'bundles',
        'bestSellers',
        'todaysChoices'
    ];
    const [states, setStates] = useState<StateProps>({
        isLoading: false,
        data: [],
        selectedLayout: null,
        showEditModel: false,
        error: null,
        updatedTitle: null,
        showUpdateModel: false,
        statusToUpdate: null
    });

    const handleChangeState = (newState: Partial<StateProps>) => {
        setStates((prev) => ({
            ...prev,
            ...newState,
        }));
    };


    const fetchLayout = async () => {
        try {
            handleChangeState({ isLoading: true });
            const PATH = `${API_URL_CMS_SERVICE}${API_ENDPOINT.homeLayout}`;
            const res = await callApi<IAppLayout[]>(PATH, { method: "GET" });
            const filtered = res.filter(i => includedKeys.includes(i.key));
            handleChangeState({ data: filtered, isLoading: false });
        } catch (error) {
            ThrowError(error);
            handleChangeState({ isLoading: false });
        }
    };

    const callUpdateApi = async (data: IAppLayout[]) => {
        try {
            const PATH = `${API_URL_CMS_SERVICE}${API_ENDPOINT.homeLayout}`;
            await callApi(PATH, {
                method: "PUT",
                data: { sections: data },
            });
            fetchLayout()
            handleChangeState({ selectedLayout: null, })
        } catch (error) {
            ThrowError(error)
        }
    }

    const updateRank = async (dragged: IAppLayout, target: IAppLayout) => {
        const dataCopy = [...states.data];

        const fromIndex = dataCopy.findIndex(item => item.id === dragged.id);
        const toIndex = dataCopy.findIndex(item => item.id === target.id);

        const [movedItem] = dataCopy.splice(fromIndex, 1);

        const newIndex = fromIndex < toIndex ? toIndex : toIndex + 1;
        dataCopy.splice(newIndex, 0, movedItem);

        const updatedData = dataCopy.map((item, index) => ({
            ...item,
            rank: index + 1,
        }));

        callUpdateApi(updatedData);
    };

    const layoutActionConfig: Record<string, ActionConfig<IAppLayout>> = {
        eidt: {
            text: 'Edit',
            onClick: (item) => handleChangeState({ selectedLayout: item, updatedTitle: item.title, showEditModel: true }),
            permission: () => canEdit(getPermissionName(PERMISSIONS.EDIT_SETTINGS)),
        },
    };

    const actions = useMemo(
        () =>
            Object.values(layoutActionConfig)
                .filter((action) => action.permission())
                .map(({ permission, ...rest }) => rest),
        [canEdit],
    );

    useEffect(() => {
        fetchLayout();
    }, []);

    const columns: TableColumn<IAppLayout>[] = [
        {
            key: 'title',
            header: 'Title',
            className: 'py-6!'
        },
        {
            key: 'key',
            header: 'Key'
        },
        {
            key: 'rank',
            header: 'Rank'
        },
        {
            key: 'viewType',
            header: 'View Type'
        },
        {
            key: 'isEnabled',
            header: 'Status',
            render: (i) => {
                return (
                    <StatusDropdown
                        onChange={(val) => { handleChangeState({ selectedLayout: i, showUpdateModel: true, statusToUpdate: val }) }}
                        options={[
                            {
                                label: 'enable', value: 'enable',
                                textClass: 'text-green-500', bgClass: 'bg-green-500'
                            },
                            {
                                label: 'disable', value: 'disable',
                                textClass: 'text-red-500', bgClass: 'bg-red-500'
                            },
                        ]}
                        canEditVal={getPermissionName(PERMISSIONS.EDIT_SETTINGS)}
                        value={i.isEnabled ? 'enable' : 'disable'}
                    />
                )
            }
        },
    ];

    const updateData = (newTitle: string | null, newStatus: string | null) => {
        if (!states.selectedLayout) return;
        const payload: IAppLayout = {
            ...states.selectedLayout,
            title: newTitle !== null ? newTitle : states.selectedLayout.title,
            isEnabled: newStatus !== null ? newStatus === 'enable' ? true : false : states.selectedLayout.isEnabled,
        };

        callUpdateApi([payload]);
        handleChangeState({ showEditModel: false, selectedLayout: null });
    };

    const handleChangeTitle = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        handleChangeState({ updatedTitle: value });

        if (!value || value.length < 3) {
            handleChangeState({ error: 'Please enter variant name atleast 3 char.' });
        } else {
            handleChangeState({ error: '' });
        }
    }

    return (
        <Layout>
            <Card>
                <CommonTable
                    columns={columns}
                    loading={states.isLoading}
                    data={states.data}
                    actions={actions}
                    getItemId={row => row.id}
                    onDropRow={(item1, item2) => updateRank(item1, item2)}
                />

                {
                    states.showEditModel && (
                        <Model
                            show={states.showEditModel}
                            onClose={() => handleChangeState({ showEditModel: false, selectedLayout: null })}
                            title="Update Layout"
                        >
                            <div className="p-4 h-full flex flex-col justify-between space-y-4">
                                <div>
                                    <FormField
                                        value={states.updatedTitle as string}
                                        onChange={(e) => handleChangeTitle(e as React.ChangeEvent<HTMLInputElement>)}
                                        placeholder="Enter variant name"
                                        required
                                    />
                                    {states.error && <span className={`text-red-500 text-sm flex items-start gap-1 mt-1 `}><Info size={16} /> {states.error}</span>}
                                </div>
                                <div className="flex justify-end space-x-4">
                                    <button
                                        className="btn-secondary"
                                        onClick={() => handleChangeState({ showEditModel: false, selectedLayout: null })}
                                        disabled={states.isLoading}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => updateData(states.updatedTitle, null)}
                                        className="btn-primary"
                                        disabled={states.isLoading}
                                    >
                                        {states.isLoading ? (
                                            <span>Submitting...</span>
                                        ) : (
                                            <>
                                                <Plus size={16} /> Update
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </Model>
                    )
                }
                {
                    states.showUpdateModel && (
                        <ConfirmationModel
                            modelType='update'
                            show={states.showUpdateModel}
                            onClose={() => { handleChangeState({ showUpdateModel: false, selectedLayout: null }) }}
                            subTitle={`This action will update the status to ${states.statusToUpdate}`}
                            onConfirm={() => updateData(null, states.statusToUpdate as string)}
                        />
                    )
                }
            </Card>
        </Layout>
    );
};

export default AppLayout;
